/**
 * OK FBI Sound System — procedural cinematic sound effects via Web Audio API.
 * Adds:
 *   - Case-specific ambience beds (subtle looping textures) with ON/OFF + volume.
 *   - Global investigation SFX toggle.
 *   - All audio is procedural (no external files) — perfectly OBS-friendly.
 */

let ctx = null;
let masterGain = null;    // Global master gain connected to ctx.destination
let sfxGain = null;       // SFX channel connected to masterGain
let ambienceGain = null;  // Ambience channel connected to masterGain
let ambienceNodes = [];   // active audio nodes for the current bed
let currentBed = null;    // case_id currently playing
let sfxEnabled = true;
let ambienceEnabled = true;
let ambienceVolume = 0.12; // 12% default (10–15% range)

function ensureCtx() {
  if (!ctx) {
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      masterGain = ctx.createGain();
      masterGain.gain.value = sfxEnabled ? 1.0 : 0.0;
      masterGain.connect(ctx.destination);

      sfxGain = ctx.createGain();
      sfxGain.gain.value = 0.35;
      sfxGain.connect(masterGain);

      ambienceGain = ctx.createGain();
      ambienceGain.gain.value = ambienceEnabled ? ambienceVolume : 0;
      ambienceGain.connect(masterGain);
    } catch (e) { console.warn("WebAudio unavailable", e); }
  }
  if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
}

// --- persistence ---
try {
  const s = localStorage.getItem("okfbi_sound"); if (s === "off") sfxEnabled = false;
  const a = localStorage.getItem("okfbi_amb"); if (a === "off") ambienceEnabled = false;
  const v = localStorage.getItem("okfbi_amb_vol"); if (v !== null) ambienceVolume = Math.max(0, Math.min(1, parseFloat(v) || 0.12));
} catch {}

export function setSoundEnabled(on) {
  sfxEnabled = !!on;
  try { localStorage.setItem("okfbi_sound", on ? "on" : "off"); } catch {}
  if (masterGain && ctx) {
    masterGain.gain.setTargetAtTime(on ? 1.0 : 0.0, ctx.currentTime, 0.02);
  }
  if (!on && typeof window !== "undefined" && window.speechSynthesis) {
    try { window.speechSynthesis.cancel(); } catch {}
  }
}
export function isSoundEnabled() { return sfxEnabled; }

export function setAmbienceEnabled(on) {
  ambienceEnabled = !!on;
  try { localStorage.setItem("okfbi_amb", on ? "on" : "off"); } catch {}
  if (ambienceGain && ctx) ambienceGain.gain.setTargetAtTime(on ? ambienceVolume : 0, ctx.currentTime, 0.4);
}
export function isAmbienceEnabled() { return ambienceEnabled; }

export function setAmbienceVolume(v) {
  ambienceVolume = Math.max(0, Math.min(1, v));
  try { localStorage.setItem("okfbi_amb_vol", String(ambienceVolume)); } catch {}
  if (ambienceGain && ctx && ambienceEnabled) ambienceGain.gain.setTargetAtTime(ambienceVolume, ctx.currentTime, 0.3);
}
export function getAmbienceVolume() { return ambienceVolume; }
export function getCurrentBed() { return currentBed; }

// ============ Audio Ducking (used while suspects speak) ============
let ducked = false;
export function duckAmbience() {
  ducked = true;
  if (ambienceGain && ctx) {
    // Drop to ~20% of current level over 250ms
    ambienceGain.gain.setTargetAtTime((ambienceEnabled ? ambienceVolume * 0.2 : 0), ctx.currentTime, 0.25);
  }
}
export function unduckAmbience() {
  ducked = false;
  if (ambienceGain && ctx) {
    ambienceGain.gain.setTargetAtTime((ambienceEnabled ? ambienceVolume : 0), ctx.currentTime, 0.35);
  }
}
if (typeof window !== "undefined") {
  window.addEventListener("okfbi:duck:start", duckAmbience);
  window.addEventListener("okfbi:duck:end",   unduckAmbience);
}

// ============ Primitives ============
function tone({ freq = 440, dur = 0.15, type = "sine", vol = 0.5, attack = 0.005, release = 0.08, freqEnd = null, filterFreq = null, target = null }) {
  if (!sfxEnabled && !target) return;
  const c = ensureCtx(); if (!c) return;
  const now = c.currentTime;
  const osc = c.createOscillator(); const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, now);
  if (freqEnd) osc.frequency.exponentialRampToValueAtTime(freqEnd, now + dur);
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(vol, now + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, now + dur + release);
  let dest = g;
  if (filterFreq) {
    const f = c.createBiquadFilter(); f.type = "lowpass"; f.frequency.value = filterFreq; f.Q.value = 0.7;
    g.connect(f); dest = f;
  }
  osc.connect(g); dest.connect(target || sfxGain || masterGain);
  osc.start(now); osc.stop(now + dur + release + 0.05);
}

function noise({ dur = 0.15, vol = 0.15, filter = 1200, filterType = "bandpass", Q = 1, target = null }) {
  if (!sfxEnabled && !target) return;
  const c = ensureCtx(); if (!c) return;
  const now = c.currentTime;
  const buf = c.createBuffer(1, Math.floor(c.sampleRate * dur), c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1);
  const src = c.createBufferSource(); src.buffer = buf;
  const f = c.createBiquadFilter(); f.type = filterType; f.frequency.value = filter; f.Q.value = Q;
  const g = c.createGain();
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(vol, now + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
  src.connect(f); f.connect(g); g.connect(target || sfxGain || masterGain);
  src.start(now); src.stop(now + dur + 0.05);
}

// ============ Ambience Beds (looping textures per case) ============
function makeNoiseLoop({ dur = 3, filter = 800, filterType = "lowpass", Q = 0.7, vol = 0.5 }) {
  const c = ensureCtx(); if (!c) return null;
  const buf = c.createBuffer(1, c.sampleRate * dur, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 0.3;
  const src = c.createBufferSource(); src.buffer = buf; src.loop = true;
  const f = c.createBiquadFilter(); f.type = filterType; f.frequency.value = filter; f.Q.value = Q;
  const g = c.createGain(); g.gain.value = vol;
  src.connect(f); f.connect(g); g.connect(ambienceGain);
  src.start(); return { src, f, g };
}

function makeDrone({ freq = 60, type = "sine", vol = 0.15, detune = 0 }) {
  const c = ensureCtx(); if (!c) return null;
  const o = c.createOscillator(); o.type = type; o.frequency.value = freq; o.detune.value = detune;
  const g = c.createGain(); g.gain.value = vol;
  o.connect(g); g.connect(ambienceGain);
  o.start(); return { o, g };
}

function scheduleOccasional(fn, minInterval, maxInterval) {
  let stopped = false;
  const loop = () => {
    if (stopped) return;
    const t = minInterval + Math.random() * (maxInterval - minInterval);
    setTimeout(() => { if (!stopped) { fn(); loop(); } }, t);
  };
  loop();
  return () => { stopped = true; };
}

function stopAmbience() {
  ambienceNodes.forEach(n => { try {
    if (n.src) n.src.stop(); if (n.o) n.o.stop();
    if (n.cancel) n.cancel();
  } catch {} });
  ambienceNodes = []; currentBed = null;
}

/** Play a case-specific ambience bed. Safe to call repeatedly with same id (no-op). */
export function playAmbienceFor(caseId) {
  if (!caseId) return;
  if (currentBed === caseId) return;
  const c = ensureCtx(); if (!c) return;
  stopAmbience();
  currentBed = caseId;
  const bed = AMBIENCE_BEDS[caseId] || AMBIENCE_BEDS.default;
  bed().forEach(n => n && ambienceNodes.push(n));
  if (ambienceGain) ambienceGain.gain.setTargetAtTime(ambienceEnabled ? ambienceVolume : 0, c.currentTime, 0.6);
}

export function stopAmbienceBed() { stopAmbience(); }

// Bed factories — each returns an array of active audio nodes.
const AMBIENCE_BEDS = {
  // HOTEL: air ventilation + distant elevator + soft corridor tone
  "case-001": () => {
    const nodes = [];
    nodes.push(makeNoiseLoop({ dur: 4, filter: 400, filterType: "lowpass", Q: 0.5, vol: 0.55 }));
    nodes.push(makeDrone({ freq: 55, type: "sine", vol: 0.13 }));
    nodes.push(makeDrone({ freq: 82, type: "triangle", vol: 0.06, detune: -8 }));
    const cancel = scheduleOccasional(() => {
      tone({ freq: 140, freqEnd: 90, dur: 1.4, type: "sine", vol: 0.05, filterFreq: 300, target: ambienceGain });
    }, 12000, 30000);
    nodes.push({ cancel });
    return nodes;
  },
  // NEWSROOM: AC hum + distant keyboards
  "case-002": () => {
    const nodes = [];
    nodes.push(makeNoiseLoop({ dur: 3, filter: 900, filterType: "lowpass", Q: 0.4, vol: 0.4 }));
    nodes.push(makeDrone({ freq: 72, type: "sine", vol: 0.09 }));
    const cancel = scheduleOccasional(() => {
      // subtle keyboard clicks
      for (let i = 0; i < 3 + Math.random() * 4; i++) {
        setTimeout(() => tone({ freq: 2200 + Math.random() * 400, dur: 0.015, type: "square", vol: 0.03, target: ambienceGain }), i * 120);
      }
    }, 6000, 14000);
    nodes.push({ cancel });
    return nodes;
  },
  // AUCTION HALL: large hall room tone + subtle clock tick
  "case-003": () => {
    const nodes = [];
    nodes.push(makeNoiseLoop({ dur: 4, filter: 500, filterType: "lowpass", Q: 0.6, vol: 0.5 }));
    nodes.push(makeDrone({ freq: 48, type: "sine", vol: 0.12 }));
    nodes.push(makeDrone({ freq: 96, type: "sine", vol: 0.05, detune: 6 }));
    const cancel = scheduleOccasional(() => {
      tone({ freq: 1100, dur: 0.03, type: "sine", vol: 0.06, target: ambienceGain });
    }, 1000, 1200); // clock tick ~1s
    nodes.push({ cancel });
    return nodes;
  },
  // TRAIN: rail vibration + carriage movement + metallic clacks
  "case-004": () => {
    const nodes = [];
    nodes.push(makeNoiseLoop({ dur: 5, filter: 260, filterType: "lowpass", Q: 0.9, vol: 0.85 }));
    nodes.push(makeDrone({ freq: 38, type: "sawtooth", vol: 0.14, detune: -6 }));
    nodes.push(makeDrone({ freq: 76, type: "triangle", vol: 0.06 }));
    const cancel = scheduleOccasional(() => {
      tone({ freq: 55, dur: 0.06, type: "square", vol: 0.09, filterFreq: 220, target: ambienceGain });
      setTimeout(() => tone({ freq: 62, dur: 0.06, type: "square", vol: 0.08, filterFreq: 220, target: ambienceGain }), 200);
    }, 1800, 3200);
    nodes.push({ cancel });
    return nodes;
  },
  // VILLA at night: wind + distant thunder + crickets
  "case-005": () => {
    const nodes = [];
    nodes.push(makeNoiseLoop({ dur: 6, filter: 320, filterType: "lowpass", Q: 0.4, vol: 0.6 }));
    nodes.push(makeDrone({ freq: 42, type: "sine", vol: 0.08 }));
    const cancel1 = scheduleOccasional(() => {
      // cricket chirps
      for (let i = 0; i < 2 + Math.random() * 3; i++) {
        setTimeout(() => tone({ freq: 3600 + Math.random() * 400, dur: 0.03, type: "sine", vol: 0.04, target: ambienceGain }), i * 90);
      }
    }, 4000, 9000);
    const cancel2 = scheduleOccasional(() => {
      // distant thunder — very rare
      tone({ freq: 60, freqEnd: 40, dur: 2.5, type: "sawtooth", vol: 0.08, filterFreq: 180, target: ambienceGain });
    }, 40000, 90000);
    nodes.push({ cancel: () => { cancel1(); cancel2(); } });
    return nodes;
  },
  default: () => {
    return [makeNoiseLoop({ dur: 3, filter: 500, filterType: "lowpass", Q: 0.4, vol: 0.35 })];
  }
};

// ============ Investigation SFX ============
export const sfx = {
  fileOpen() {
    tone({ freq: 220, freqEnd: 120, dur: 0.18, type: "triangle", vol: 0.25, filterFreq: 900 });
    setTimeout(() => noise({ dur: 0.12, vol: 0.08, filter: 400, filterType: "lowpass" }), 60);
  },
  pageFlip() { noise({ dur: 0.09, vol: 0.09, filter: 2400, filterType: "highpass", Q: 0.5 }); },
  evidenceReveal() {
    tone({ freq: 660, dur: 0.09, type: "sine", vol: 0.22 });
    setTimeout(() => tone({ freq: 990, dur: 0.14, type: "sine", vol: 0.18 }), 90);
  },
  evidenceUndo() { tone({ freq: 500, freqEnd: 320, dur: 0.14, type: "triangle", vol: 0.2 }); },
  fingerprint() {
    tone({ freq: 180, freqEnd: 320, dur: 0.35, type: "sawtooth", vol: 0.14, filterFreq: 800 });
    setTimeout(() => tone({ freq: 880, dur: 0.05, type: "square", vol: 0.12 }), 340);
  },
  dnaScan() {
    for (let i = 0; i < 3; i++) setTimeout(() => tone({ freq: 520 + i * 80, dur: 0.08, type: "sine", vol: 0.16 }), i * 90);
  },
  cctvBeep() { tone({ freq: 1200, dur: 0.05, type: "square", vol: 0.14 }); },
  cctvOpen() {
    // CRT power-on: rising sweep + click
    tone({ freq: 90, freqEnd: 480, dur: 0.32, type: "sawtooth", vol: 0.14, filterFreq: 900 });
    setTimeout(() => tone({ freq: 1600, dur: 0.03, type: "square", vol: 0.1 }), 320);
  },
  cctvSwitch() {
    // Camera switch: quick static burst + beep
    noise({ dur: 0.05, vol: 0.12, filter: 3000, filterType: "highpass", Q: 0.4 });
    setTimeout(() => tone({ freq: 900, dur: 0.04, type: "square", vol: 0.1 }), 30);
  },
  cctvCapture() {
    // Shutter-style: sharp click + confirm chime
    tone({ freq: 2400, dur: 0.02, type: "square", vol: 0.14 });
    setTimeout(() => tone({ freq: 1200, dur: 0.02, type: "square", vol: 0.1 }), 30);
    setTimeout(() => { tone({ freq: 660, dur: 0.06, type: "sine", vol: 0.16 });
                       setTimeout(() => tone({ freq: 990, dur: 0.1, type: "sine", vol: 0.14 }), 60); }, 90);
  },
  audioEnhance() { tone({ freq: 400, freqEnd: 800, dur: 0.22, type: "sine", vol: 0.18 }); },
  notification() {
    tone({ freq: 880, dur: 0.09, type: "sine", vol: 0.2 });
    setTimeout(() => tone({ freq: 1320, dur: 0.14, type: "sine", vol: 0.18 }), 100);
  },
  voteLock() {
    tone({ freq: 130, dur: 0.15, type: "sine", vol: 0.3, filterFreq: 500 });
    noise({ dur: 0.08, vol: 0.1, filter: 300, filterType: "lowpass" });
  },
  voteChime(vol = 1) {
    // Subtle premium investigation-interface chime — two sine tones + very light click.
    // vol is 0..1 host-controlled multiplier. Keep peak below 0.16 for OBS-safe levels.
    const v = Math.max(0, Math.min(1, vol));
    tone({ freq: 1320, dur: 0.03, type: "square", vol: 0.05 * v });
    setTimeout(() => tone({ freq: 880, dur: 0.09, type: "sine", vol: 0.11 * v }), 25);
    setTimeout(() => tone({ freq: 1320, dur: 0.13, type: "sine", vol: 0.08 * v, filterFreq: 3000 }), 90);
  },
  accusation() {
    tone({ freq: 220, freqEnd: 165, dur: 0.35, type: "sawtooth", vol: 0.18, filterFreq: 700 });
    setTimeout(() => tone({ freq: 110, dur: 0.5, type: "sine", vol: 0.25 }), 300);
  },
  caseSolved() {
    tone({ freq: 82, dur: 0.9, type: "sine", vol: 0.4 });
    tone({ freq: 165, dur: 0.9, type: "triangle", vol: 0.22, filterFreq: 500 });
    setTimeout(() => noise({ dur: 0.4, vol: 0.12, filter: 200, filterType: "lowpass" }), 20);
    setTimeout(() => tone({ freq: 660, dur: 0.5, type: "sine", vol: 0.18 }), 350);
    setTimeout(() => tone({ freq: 880, dur: 0.6, type: "sine", vol: 0.15 }), 500);
  },
  caseFailed() {
    tone({ freq: 220, freqEnd: 90, dur: 1.2, type: "sawtooth", vol: 0.22, filterFreq: 380 });
    setTimeout(() => tone({ freq: 82, dur: 0.9, type: "sine", vol: 0.3 }), 200);
  },
  click() { tone({ freq: 660, dur: 0.03, type: "square", vol: 0.08 }); },
  tabSwitch() { tone({ freq: 880, dur: 0.04, type: "sine", vol: 0.1 }); },
};

// Unlock audio on first user interaction (browser policy)
if (typeof window !== "undefined") {
  const unlock = () => { ensureCtx(); window.removeEventListener("click", unlock); window.removeEventListener("keydown", unlock); };
  window.addEventListener("click", unlock, { once: true });
  window.addEventListener("keydown", unlock, { once: true });
}
